import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, Subject, filter, tap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { Document } from 'src/app/shared/document';
import { ClientApiService } from 'src/app/services/client-api.service';

/**
 * @description
 * Renders the seller-side (main-company / LCO) party panel of an edit-document form.
 * Accepts a `Document` input and writes the user's selections directly into the shared
 * document object fields: `DOCMANPERID`, `DOCMANDEPID`, `DOCLCOCLIID`, `DOCLCOPERID`,
 * `DOCLCODEPID`.
 *
 * When the main stock depot changes (`mainStockSelected`), the component automatically
 * resolves the matching LCO child department via
 * `ClientApiService.getChildDepartmentsById()` and patches `DOCLCODEPID` in place.
 *
 * All API subscriptions use `takeUntil(destroy$)` for automatic teardown on destroy.
 *
 * ### Inputs
 * | Input          | Type     | Description                            |
 * |----------------|----------|----------------------------------------|
 * | `document`     | Document | The shared document being edited       |
 * | `manCliLabel`  | string   | Label for main-company selector        |
 * | `manPerLabel`  | string   | Label for main-contact selector        |
 * | `manDepLabel`  | string   | Label for main-stock selector          |
 * | `lcoCliLabel`  | string   | Label for LCO company selector         |
 * | `lcoPerLabel`  | string   | Label for LCO contact selector         |
 * | `lcoDepLabel`  | string   | Label for LCO stock selector           |
 */
@Component({
  selector: 'app-edit-document-company',
  templateUrl: './edit-document-company.component.html',
  styleUrls: ['./edit-document-company.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentCompanyComponent implements OnInit, OnChanges, OnDestroy {

  @Input() document: Document;

  @Input() manCliLabel: string = 'Основная организация продавца';
  @Input() manPerLabel: string = 'Контактное лицо';
  @Input() manDepLabel: string = 'Основной склад';
  @Input() lcoCliLabel: string = 'Юридическое лицо продавца';
  @Input() lcoPerLabel: string = 'Ответственный';
  @Input() lcoDepLabel: string = 'Склад';

  private _depChildQuery: Subscription;
  private destroy$ = new Subject<void>();

  mainCompanyId: number;

  constructor(
    private cliApi: ClientApiService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** EditDocumentCompanyComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentCompanyComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** EditDocumentCompanyComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'document':

          if (this.mainCompanyId !== change.currentValue.MANCLIID) {
            this.mainCompanyId = change.currentValue.MANCLIID;
          }

          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** EditDocumentCompanyComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    console.log('*** EditDocumentCompanyComponent in ngOnInit')
  }

  /** @description Called when the user selects a new main company. Placeholder — no document field is updated here; child selectors re-render on the next document change cycle. */
  mainCompanySelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in mainCompanySelected: ' + selectedId);
  }

  /** @description Called when the user selects a contact person for the main company. Writes `selectedId` to `document.DOCMANPERID`. */
  mainPersonSelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in mainPersonSelected: ' + selectedId);

    this.document.DOCMANPERID = selectedId;
  }

  /**
   * @description
   * Called when the user selects a main stock depot. Sets `document.DOCMANDEPID` to
   * `selectedId`, then queries `ClientApiService.getChildDepartmentsById()` to resolve
   * the matching LCO child department and patches `document.DOCLCODEPID` automatically.
   * The subscription is torn down via `takeUntil(destroy$)` and errors are logged to console.
   */
  mainStockSelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in mainStockSelected: ' + selectedId);

    this.document.DOCMANDEPID = selectedId;

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCMANDEPID, this.document.DOCLCOCLIID)
      .pipe(
        tap(data => console.log('*** EditDocumentCompanyComponent _depChildQuery ' + JSON.stringify(data))),
        filter(data => data && data.length > 0),
        map(data => this.document.DOCLCODEPID = data[0].CLIID),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: err => console.error('*** EditDocumentCompanyComponent _depChildQuery error:', err)
      });
  }

  /** @description Called when the user selects an LCO (legal entity) company. Writes `selectedId` to `document.DOCLCOCLIID`. */
  lcoCompanySelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in lcoCompanySelected: ' + selectedId);
    this.document.DOCLCOCLIID = selectedId;
  }

  /** @description Called when the user selects a contact person for the LCO entity. Writes `selectedId` to `document.DOCLCOPERID`. */
  lcoPersonSelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in lcoPersonSelected: ' + selectedId);
    this.document.DOCLCOPERID = selectedId;
  }

  /** @description Called when the user selects an LCO stock depot. Writes `selectedId` to `document.DOCLCODEPID`. */
  lcoStockSelected(selectedId) {
    console.log('*** EditDocumentCompanyComponent in lcoStockSelected: ' + selectedId);
    this.document.DOCLCODEPID = selectedId;
  }
}
